import { Environment } from '../enums/Environment';
import { Logger } from '../exports';
import { VERSION } from '../version';
import { CommonConstants } from './CommonConstants';
import { EnvProcess } from './EnvProcess';
import { ReplaceLocalhostWithIp } from './Helpers';

/**
 * Shared settings (ports, URIs, auth) used by apps, services, and scripts. Also see `src\services\engine\src\common\GetServiceSettings.ts` for protected service-only settings.
 *
 * - Development: localhost-based URIs; backend services reach each other through Kong.
 * - Test / Production: fixed cloud URIs routed through Kong.
 * - `ENV_MODE` is injected per service at deploy time by the generated GCP dockerfile and workflow (`ARG ENV_MODE` / `ENV ENV_MODE`, passed by `pnpm build:deploy`).
 */

/** File-local constants for GetSharedSettings. */
const localConstants = {
  logContext: 'GetSharedSettings',
  assetsContainerHost: 'vertex-echo-infra-assets-main',
  vapiWebhookPath: '/api/voice/webhook/vapi',
  vapiToolsPath: '/api/voice/tools/calls',
  twilioWebhookPath: '/api/automation/webhook/twilio',
  stripeWebhookPath: '/api/billing/webhook/stripe',
  // Public redirect URI registered with every scheduling OAuth app. The lobby OAuth controller handles the callback; the path is shared by the authorize URL and the token exchange.
  schedulingOAuthCallbackPath: '/api/lobby/integrations/scheduling/callback'
} as const;

/** Shape of the resolved shared settings map for any environment. */
export type SharedSettingsMap = {
  // PORTS
  // infrastructure
  INFRA_POSTGRES_PORT: number;
  INFRA_MONGO_PORT: number;
  INFRA_REDIS_PORT: number;
  INFRA_KONG_PORT: number;
  // services
  SERVICE_LOBBY_PORT: number;
  SERVICE_USER_PORT: number;
  SERVICE_VOICE_PORT: number;
  SERVICE_AUTOMATION_PORT: number;
  SERVICE_BILLING_PORT: number;
  SERVICE_SEEDER_PORT: number;
  // apps
  APP_PORTAL_PORT: number;
  APP_LOBBY_PORT: number;
  // assets
  ASSETS_PORT: number;
  // URIS
  // infrastructure
  INFRA_POSTGRES_HOST: string;
  INFRA_POSTGRES_URI: string;
  INFRA_MONGO_HOST: string;
  INFRA_MONGO_URI: string;
  INFRA_REDIS_HOST: string;
  INFRA_REDIS_URI: string;
  INFRA_KONG_URI: string;
  // backups (S3-compatible storage; GCS in cloud, MinIO locally)
  INFRA_S3_URI: string;
  INFRA_BACKUPS_BUCKET: string;
  INFRA_PGBACKREST_BUCKET: string;
  INFRA_MONGO_RS_HOST: string;
  // services
  SERVICE_LOBBY_URI: string;
  SERVICE_USER_URI: string;
  SERVICE_VOICE_URI: string;
  SERVICE_AUTOMATION_URI: string;
  SERVICE_BILLING_URI: string;
  SERVICE_SEEDER_URI: string;
  // service internal uris
  SERVICE_INTERNAL_LOBBY_URI: string;
  SERVICE_INTERNAL_USER_URI: string;
  SERVICE_INTERNAL_VOICE_URI: string;
  SERVICE_INTERNAL_AUTOMATION_URI: string;
  SERVICE_INTERNAL_BILLING_URI: string;
  SERVICE_INTERNAL_SEEDER_URI: string;
  // apps
  APP_PORTAL_URI: string;
  APP_LOBBY_URI: string;
  // assets
  ASSETS_URI: string;
  //AUTH
  AUTH_API_KEY: string;
  AUTH_DOMAIN: string;
  AUTH_PROJECT_ID: string;
  AUTH_APP_ID: string;
  AUTH_MEASUREMENT_ID: string;
  // provider endpoints
  VAPI_SERVER_URL: string;
  VAPI_TOOLS_URL: string;
  TWILIO_STATUS_CALLBACK_URL: string;
  STRIPE_WEBHOOK_URL: string;
  // Public redirect URI for the per-tenant scheduling OAuth flow (Google/Outlook). Composed through Kong; in Development it resolves from DEV_TUNNEL_URL at runtime.
  SCHEDULING_OAUTH_REDIRECT_URL: string;
  BILLING_CHECKOUT_SUCCESS_URL: string;
  BILLING_CHECKOUT_CANCEL_URL: string;
  BILLING_PORTAL_RETURN_URL: string;
  STORAGE_ENDPOINT: string;
  // ENVIRONMENT
  ENV_MODE: string;
  VERSION: string;
};

/** Kong upstream rewrite descriptor: replace `FROM_URI` with `TO_URI` everywhere it appears in the generated Kong config. */
export type DevKongOverride = {
  /** Docker-DNS URI to replace (matches a `SERVICE_INTERNAL_<NAME>_URI` from `config.Development`). */
  FROM_URI: string;
  /** Host-bridge URI Kong should call instead. */
  TO_URI: string;
};

/**
 * Port assignments shared across all environments.
 *
 * `APP_*_PORT` values are consumed only by each frontend's `vite.config.ts` (`server.port` / `preview.port`) so per-workspace vite runs don't collide on the host. They do NOT compose frontend
 * URIs — all `APP_*_URI` values resolve through `ASSETS_PORT` (Development) or `BaseTestURL` / `BaseProdURL` (Test / Production).
 */
const sharedPorts: Record<string, number> = {
  // INFRASTRUCTURE
  INFRA_POSTGRES_PORT: 5432,
  INFRA_REDIS_PORT: 6379,
  INFRA_KONG_PORT: 8000,
  INFRA_MONGO_PORT: 27017,
  // SERVICES
  SERVICE_LOBBY_PORT: 3000,
  SERVICE_USER_PORT: 3002,
  SERVICE_VOICE_PORT: 3007,
  SERVICE_AUTOMATION_PORT: 3008,
  SERVICE_BILLING_PORT: 3009,
  SERVICE_SEEDER_PORT: 3006,
  // APPS
  APP_PORTAL_PORT: 4001,
  APP_LOBBY_PORT: 4000,
  // ASSETS
  ASSETS_PORT: 5000
};

/** Per-environment port assignments. Each environment inherits from `sharedPorts`; override individual ports per environment if they ever need to differ. */
const ports: Record<string, Record<string, number>> = {
  Development: { ...sharedPorts },
  Test: { ...sharedPorts },
  Production: { ...sharedPorts }
};

/** Per-environment URI tables: connection strings, service endpoints, auth credentials. Selected by `ENV_MODE` at lookup time. */
const config: Record<string, Record<string, string>> = {
  Development: {
    // INFRASTRUCTURE
    // postgres
    INFRA_POSTGRES_HOST: `localhost`,
    INFRA_POSTGRES_URI: `postgres://localhost:${ports.Development.INFRA_POSTGRES_PORT}`,
    // mongo
    INFRA_MONGO_HOST: `localhost`,
    INFRA_MONGO_URI: `mongodb://localhost:${ports.Development.INFRA_MONGO_PORT}`,
    // redis (HOST is the Docker DNS name — only consumed by Kong config templates; services dial via URI)
    INFRA_REDIS_HOST: 'vertex-echo-infra-redis-main',
    INFRA_REDIS_URI: `redis://localhost:${ports.Development.INFRA_REDIS_PORT}`,
    // backups (local MinIO, S3-compatible). HTTPS with a self-signed cert from the minio-certs init container; pgBackRest's S3 driver requires TLS. All clients skip cert verification locally
    // (`NODE_TLS_REJECT_UNAUTHORIZED=0` for AWS SDK, `--insecure` for mc, `repo1-storage-verify-tls=n` for pgBackRest). Cloud uses GCS with a real cert.
    INFRA_S3_URI: `https://vertex-echo-infra-minio-main:9000`,
    INFRA_BACKUPS_BUCKET: `vertex-echo-backups-main`,
    INFRA_PGBACKREST_BUCKET: `vertex-echo-pgbackrest-main`,
    INFRA_MONGO_RS_HOST: `vertex-echo-infra-mongodb-main:${ports.Development.INFRA_MONGO_PORT}`,
    // kong
    INFRA_KONG_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}`,
    // SERVICES
    SERVICE_LOBBY_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}/api/lobby`,
    SERVICE_USER_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}/api/user`,
    SERVICE_VOICE_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}/api/voice`,
    SERVICE_AUTOMATION_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}/api/automation`,
    SERVICE_BILLING_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}/api/billing`,
    SERVICE_SEEDER_URI: `http://localhost:${sharedPorts.INFRA_KONG_PORT}/api/seeder`,
    // SERVICE INTERNAL URIS
    SERVICE_INTERNAL_LOBBY_URI: `http://vertex-echo-service-lobby-main:${sharedPorts.SERVICE_LOBBY_PORT}`,
    SERVICE_INTERNAL_USER_URI: `http://vertex-echo-service-user-main:${sharedPorts.SERVICE_USER_PORT}`,
    SERVICE_INTERNAL_VOICE_URI: `http://vertex-echo-service-voice-main:${sharedPorts.SERVICE_VOICE_PORT}`,
    SERVICE_INTERNAL_AUTOMATION_URI: `http://vertex-echo-service-automation-main:${sharedPorts.SERVICE_AUTOMATION_PORT}`,
    SERVICE_INTERNAL_BILLING_URI: `http://vertex-echo-service-billing-main:${sharedPorts.SERVICE_BILLING_PORT}`,
    SERVICE_INTERNAL_SEEDER_URI: `http://vertex-echo-infra-seeder-main:${sharedPorts.SERVICE_SEEDER_PORT}`,
    // APPS — single canonical URL per frontend, served by the assets container's nginx (`localhost:${ASSETS_PORT}/web/<area>/<name>`). Mirrors the cloud `${BaseProdURL|BaseTestURL}/web/...`
    // path. When a frontend is self-launched, `launch.ts` injects an nginx `location ^~` block in the assets container that proxies the same path to the host-bound vite dev server, so the
    // URL stays canonical in every launch mode.
    APP_PORTAL_URI: `http://localhost:${sharedPorts.ASSETS_PORT}/web/apps/portal`,
    APP_LOBBY_URI: `http://localhost:${sharedPorts.ASSETS_PORT}/web/apps/lobby`,
    // ASSETS
    ASSETS_URI: `http://localhost:${sharedPorts.ASSETS_PORT}`,
    // AUTH
    AUTH_API_KEY: 'AIzaSyCJdisMUYg9nV_-kBk3CqRbQB8NiuxF5do',
    AUTH_DOMAIN: 'test-vertex-echo-auth.firebaseapp.com',
    AUTH_PROJECT_ID: 'test-vertex-echo-auth',
    AUTH_APP_ID: '1:340472007417:web:50418fdfa64c66af887028',
    AUTH_MEASUREMENT_ID: 'G-MLB44LD3T3',
    // PROVIDER ENDPOINTS
    // Empty by default: Vapi is a cloud service and cannot reach `localhost`, so locally a number/assistant is provisioned WITHOUT a `server.url` (see `VapiVoiceProvider.buildServerConfig`) and
    // inbound voice webhooks are simply never delivered to the dev stack. To exercise the real webhook flow locally, front local Kong (`localhost:8000`) with the public cloudflared tunnel routed at
    // `DEV_TUNNEL_URL`; `GetSharedSettings` then composes the full callback at runtime (see the `DEV_TUNNEL_URL` block below).
    // `DEV_TUNNEL_URL` is local-dev only: it lives in `.secrets`, is injected into the billing/voice/automation containers, and `pnpm launch` auto-starts the cloudflared tunnel to local Kong. It is
    // NOT part of `SharedSettingsMap` and is never set in Test/Production (those use the fixed Kong URLs above). After it first resolves, re-provision the number/assistant so Vapi re-stamps the new
    // callback. The inbound `x-vapi-secret` header is still checked against `VAPI_WEBHOOK_SECRET` (already in `.secrets`), so authentication works the moment events start arriving.
    VAPI_SERVER_URL: '',
    // Empty by default for the same reason as `VAPI_SERVER_URL`: the in-call tool dispatcher (`/api/voice/tools/calls`) is stamped onto each transient-assistant tool's `server.url`, and Vapi (a
    // cloud service) cannot reach `localhost`. When empty the voice service omits `model.tools` from the assistant-request response, so in-call function calling is simply inactive locally; setting
    // `DEV_TUNNEL_URL` composes the public tools URL (see the `DEV_TUNNEL_URL` block below) so in-call tools round-trip against the local stack through the same tunnel that exercises the webhook.
    VAPI_TOOLS_URL: '',
    // Empty by default for the same reason as `VAPI_SERVER_URL`: Twilio is a cloud service and cannot post SMS delivery-status callbacks to `localhost`. When empty the provider simply omits the
    // `StatusCallback` param so no callback is registered (see `TwilioNotificationProvider`). Setting `DEV_TUNNEL_URL` makes `GetSharedSettings` compose `<tunnel>/api/automation/webhook/twilio` at
    // runtime (see the `DEV_TUNNEL_URL` block below), so the same tunnel that exercises Vapi webhooks also delivers Twilio status callbacks locally.
    TWILIO_STATUS_CALLBACK_URL: '',
    // Empty by default for the same reason as `VAPI_SERVER_URL`: Stripe is a cloud service and cannot reach `localhost`. With `DEV_TUNNEL_URL` set (the case once `.secrets` carries it),
    // `GetSharedSettings` composes `<tunnel>/api/billing/webhook/stripe` so a manually provisioned Stripe webhook can target the local stack. Without `DEV_TUNNEL_URL` the URL stays empty and
    // verification remains offline locally unless the environment provides a reachable endpoint.
    STRIPE_WEBHOOK_URL: '',
    // Empty by default like the webhook callbacks above: scheduling OAuth providers are cloud services and cannot redirect back to `localhost`. With `DEV_TUNNEL_URL` set, `GetSharedSettings` composes
    // `<tunnel>/api/lobby/integrations/scheduling/callback` at runtime (see the `DEV_TUNNEL_URL` block below) so the per-tenant connect flow round-trips against the local stack through the tunnel.
    SCHEDULING_OAUTH_REDIRECT_URL: '',
    // Composed against the assets-container path (matching `APP_LOBBY_URI` and the Test/Production billing URLs) so the
    // Stripe-hosted checkout returns to the lobby app's `BillingReturn` route under the same `/web/apps/lobby` basename in every launch mode.
    // The bare `localhost:4000/billing` form did not resolve to the lobby app (wrong port, missing app basename), so cancel/return dead-ended.
    BILLING_CHECKOUT_SUCCESS_URL: `http://localhost:${sharedPorts.ASSETS_PORT}/web/apps/lobby/billing?checkout=success`,
    BILLING_CHECKOUT_CANCEL_URL: `http://localhost:${sharedPorts.ASSETS_PORT}/web/apps/lobby/billing?checkout=cancel`,
    BILLING_PORTAL_RETURN_URL: `http://localhost:${sharedPorts.ASSETS_PORT}/web/apps/lobby/billing`,
    // PRODUCT object-storage endpoint consumed by `S3StorageAdapter` (KB documents, call recordings). In dev it points at the local MinIO container — the same S3-compatible endpoint that backs
    // the backup/restore flow — so local development exercises the real S3 code paths instead of silently falling through to AWS defaults (the old empty value pointed the SDK at real AWS, where
    // placeholder dev creds failed confusingly). MinIO serves HTTPS with a self-signed cert, so `S3StorageAdapter` skips TLS verification for THIS client in Development only (never process-wide).
    // Credentials are the non-secret MinIO root credentials hardcoded as GetServiceSettings code defaults (`STORAGE_ACCESS_KEY`/`STORAGE_SECRET_KEY` = `minioadmin`, like the other local dev
    // credentials); the `STORAGE_BUCKET` is created by the MinIO init container. Test and
    // Production override this with GCS (`https://storage.googleapis.com`, real cert).
    // Base is `localhost:9000` so host processes (the seeder during `pnpm launch:clean`, scripts, vite dev) reach the published MinIO port; `containerizedDevOverrides` swaps it for the
    // `vertex-echo-infra-minio-main` Docker DNS name inside containers — exactly the localhost-base / docker-DNS-override pattern used for Postgres and Mongo above. The self-signed cert SANs cover
    // both `localhost`/`127.0.0.1` and the container DNS name, so TLS-skip works from either side.
    STORAGE_ENDPOINT: `https://localhost:9000`
  },
  Test: {
    // INFRASTRUCTURE
    // postgres
    INFRA_POSTGRES_HOST: `vertex-echo-infra-postgres-test.internal`,
    INFRA_POSTGRES_URI: `postgres://vertex-echo-infra-postgres-test.internal:${ports.Test.INFRA_POSTGRES_PORT}`,
    // mongo
    INFRA_MONGO_HOST: `vertex-echo-infra-mongodb-test.internal`,
    INFRA_MONGO_URI: `mongodb://vertex-echo-infra-mongodb-test.internal:${ports.Test.INFRA_MONGO_PORT}`,
    // redis
    INFRA_REDIS_HOST: 'vertex-echo-infra-redis-test.internal',
    INFRA_REDIS_URI: `redis://vertex-echo-infra-redis-test.internal:${ports.Test.INFRA_REDIS_PORT}`,
    // backups (S3-compatible cloud object storage; AWS_ENDPOINT_URL_S3 is auto-injected per-app, this is the canonical reference)
    INFRA_S3_URI: `https://storage.googleapis.com`,
    INFRA_BACKUPS_BUCKET: `vertex-echo-backups-test`,
    INFRA_PGBACKREST_BUCKET: `vertex-echo-pgbackrest-test`,
    INFRA_MONGO_RS_HOST: `vertex-echo-infra-mongodb-test.internal:${ports.Test.INFRA_MONGO_PORT}`,
    // kong
    INFRA_KONG_URI: CommonConstants.BaseKongTestURL,
    // SERVICES
    SERVICE_LOBBY_URI: `${CommonConstants.BaseKongTestURL}/api/lobby`,
    SERVICE_USER_URI: `${CommonConstants.BaseKongTestURL}/api/user`,
    SERVICE_VOICE_URI: `${CommonConstants.BaseKongTestURL}/api/voice`,
    SERVICE_AUTOMATION_URI: `${CommonConstants.BaseKongTestURL}/api/automation`,
    SERVICE_BILLING_URI: `${CommonConstants.BaseKongTestURL}/api/billing`,
    SERVICE_SEEDER_URI: `${CommonConstants.BaseKongTestURL}/api/seeder`,
    // SERVICE INTERNAL URIS
    SERVICE_INTERNAL_LOBBY_URI: `https://vertex-echo-service-lobby-test-${CommonConstants.BaseTestHash}-pd.a.run.app`,
    SERVICE_INTERNAL_USER_URI: `https://vertex-echo-service-user-test-${CommonConstants.BaseTestHash}-pd.a.run.app`,
    SERVICE_INTERNAL_VOICE_URI: `https://vertex-echo-service-voice-test-${CommonConstants.BaseTestHash}-pd.a.run.app`,
    SERVICE_INTERNAL_AUTOMATION_URI: `https://vertex-echo-service-automation-test-${CommonConstants.BaseTestHash}-pd.a.run.app`,
    SERVICE_INTERNAL_BILLING_URI: `https://vertex-echo-service-billing-test-${CommonConstants.BaseTestHash}-pd.a.run.app`,
    // The seeder is a long-running Compute Engine COS VM (kind: ComputeInstance), NOT a Cloud Run service, so it has no `*.run.app` host. Kong reaches it over the VPC at its private DNS A-record,
    // mirroring the `INFRA_*` `.internal` host pattern (postgres/mongo/redis above) on the shared seeder control-plane port.
    SERVICE_INTERNAL_SEEDER_URI: `http://vertex-echo-infra-seeder-test.internal:${sharedPorts.SERVICE_SEEDER_PORT}`,
    // APPS — composed against `CommonConstants.BaseTestURL` so a future custom-domain swap is a single-line change.
    APP_PORTAL_URI: `${CommonConstants.BaseTestURL}/web/apps/portal`,
    APP_LOBBY_URI: `${CommonConstants.BaseTestURL}/web/apps/lobby`,
    // ASSETS
    ASSETS_URI: `${CommonConstants.BaseTestURL}`,
    // AUTH
    AUTH_API_KEY: 'AIzaSyCJdisMUYg9nV_-kBk3CqRbQB8NiuxF5do',
    AUTH_DOMAIN: 'test-vertex-echo-auth.firebaseapp.com',
    AUTH_PROJECT_ID: 'test-vertex-echo-auth',
    AUTH_APP_ID: '1:340472007417:web:50418fdfa64c66af887028',
    AUTH_MEASUREMENT_ID: 'G-MLB44LD3T3',
    // PROVIDER ENDPOINTS
    VAPI_SERVER_URL: `${CommonConstants.BaseKongTestURL}${localConstants.vapiWebhookPath}`,
    VAPI_TOOLS_URL: `${CommonConstants.BaseKongTestURL}${localConstants.vapiToolsPath}`,
    TWILIO_STATUS_CALLBACK_URL: `${CommonConstants.BaseKongTestURL}${localConstants.twilioWebhookPath}`,
    STRIPE_WEBHOOK_URL: `${CommonConstants.BaseKongTestURL}${localConstants.stripeWebhookPath}`,
    SCHEDULING_OAUTH_REDIRECT_URL: `${CommonConstants.BaseKongTestURL}${localConstants.schedulingOAuthCallbackPath}`,
    BILLING_CHECKOUT_SUCCESS_URL: `${CommonConstants.BaseTestURL}/web/apps/lobby/billing?checkout=success`,
    BILLING_CHECKOUT_CANCEL_URL: `${CommonConstants.BaseTestURL}/web/apps/lobby/billing?checkout=cancel`,
    BILLING_PORTAL_RETURN_URL: `${CommonConstants.BaseTestURL}/web/apps/lobby/billing`,
    STORAGE_ENDPOINT: 'https://storage.googleapis.com'
  },
  Production: {
    // INFRASTRUCTURE
    // postgres
    INFRA_POSTGRES_HOST: `vertex-echo-infra-postgres-main.internal`,
    INFRA_POSTGRES_URI: `postgres://vertex-echo-infra-postgres-main.internal:${ports.Production.INFRA_POSTGRES_PORT}`,
    // mongo
    INFRA_MONGO_URI: `mongodb://vertex-echo-infra-mongodb-main.internal:${ports.Production.INFRA_MONGO_PORT}`,
    INFRA_MONGO_HOST: `vertex-echo-infra-mongodb-main.internal`,
    // redis
    INFRA_REDIS_HOST: 'vertex-echo-infra-redis-main.internal',
    INFRA_REDIS_URI: `redis://vertex-echo-infra-redis-main.internal:${ports.Production.INFRA_REDIS_PORT}`,
    // backups (S3-compatible cloud object storage; AWS_ENDPOINT_URL_S3 is auto-injected per-app, this is the canonical reference)
    INFRA_S3_URI: `https://storage.googleapis.com`,
    INFRA_BACKUPS_BUCKET: `vertex-echo-backups-main`,
    INFRA_PGBACKREST_BUCKET: `vertex-echo-pgbackrest-main`,
    INFRA_MONGO_RS_HOST: `vertex-echo-infra-mongodb-main.internal:${ports.Production.INFRA_MONGO_PORT}`,
    // kong
    INFRA_KONG_URI: CommonConstants.BaseKongProdURL,
    // SERVICES
    SERVICE_LOBBY_URI: `${CommonConstants.BaseKongProdURL}/api/lobby`,
    SERVICE_USER_URI: `${CommonConstants.BaseKongProdURL}/api/user`,
    SERVICE_VOICE_URI: `${CommonConstants.BaseKongProdURL}/api/voice`,
    SERVICE_AUTOMATION_URI: `${CommonConstants.BaseKongProdURL}/api/automation`,
    SERVICE_BILLING_URI: `${CommonConstants.BaseKongProdURL}/api/billing`,
    SERVICE_SEEDER_URI: `${CommonConstants.BaseKongProdURL}/api/seeder`,
    // SERVICES INTERNAL URIS
    SERVICE_INTERNAL_LOBBY_URI: `https://vertex-echo-service-lobby-main-${CommonConstants.BaseProdHash}-pd.a.run.app`,
    SERVICE_INTERNAL_USER_URI: `https://vertex-echo-service-user-main-${CommonConstants.BaseProdHash}-pd.a.run.app`,
    SERVICE_INTERNAL_VOICE_URI: `https://vertex-echo-service-voice-main-${CommonConstants.BaseProdHash}-pd.a.run.app`,
    SERVICE_INTERNAL_AUTOMATION_URI: `https://vertex-echo-service-automation-main-${CommonConstants.BaseProdHash}-pd.a.run.app`,
    SERVICE_INTERNAL_BILLING_URI: `https://vertex-echo-service-billing-main-${CommonConstants.BaseProdHash}-pd.a.run.app`,
    // The seeder is a long-running Compute Engine COS VM (kind: ComputeInstance), NOT a Cloud Run service, so it has no `*.run.app` host. Kong reaches it over the VPC at its private DNS A-record,
    // mirroring the `INFRA_*` `.internal` host pattern (postgres/mongo/redis above) on the shared seeder control-plane port.
    SERVICE_INTERNAL_SEEDER_URI: `http://vertex-echo-infra-seeder-main.internal:${sharedPorts.SERVICE_SEEDER_PORT}`,
    // APPS — composed against `CommonConstants.BaseProdURL` so a future custom-domain swap is a single-line change.
    APP_PORTAL_URI: `${CommonConstants.BaseProdURL}/web/apps/portal`,
    APP_LOBBY_URI: `${CommonConstants.BaseProdURL}/web/apps/lobby`,
    // ASSETS
    ASSETS_URI: `${CommonConstants.BaseProdURL}`,
    // AUTH
    AUTH_API_KEY: 'AIzaSyB3HkJWuOD5jEgZewlC3py7W8zV0sHeVcQ',
    AUTH_DOMAIN: 'vertexecho-auth.firebaseapp.com',
    AUTH_PROJECT_ID: 'vertexecho-auth',
    AUTH_APP_ID: '1:500813107970:web:300c960549b954ef04281b',
    AUTH_MEASUREMENT_ID: 'G-7T2D9DS8RS',
    // PROVIDER ENDPOINTS
    VAPI_SERVER_URL: `${CommonConstants.BaseKongProdURL}${localConstants.vapiWebhookPath}`,
    VAPI_TOOLS_URL: `${CommonConstants.BaseKongProdURL}${localConstants.vapiToolsPath}`,
    TWILIO_STATUS_CALLBACK_URL: `${CommonConstants.BaseKongProdURL}${localConstants.twilioWebhookPath}`,
    STRIPE_WEBHOOK_URL: `${CommonConstants.BaseKongProdURL}${localConstants.stripeWebhookPath}`,
    SCHEDULING_OAUTH_REDIRECT_URL: `${CommonConstants.BaseKongProdURL}${localConstants.schedulingOAuthCallbackPath}`,
    BILLING_CHECKOUT_SUCCESS_URL: `${CommonConstants.BaseProdURL}/web/apps/lobby/billing?checkout=success`,
    BILLING_CHECKOUT_CANCEL_URL: `${CommonConstants.BaseProdURL}/web/apps/lobby/billing?checkout=cancel`,
    BILLING_PORTAL_RETURN_URL: `${CommonConstants.BaseProdURL}/web/apps/lobby/billing`,
    STORAGE_ENDPOINT: 'https://storage.googleapis.com'
  }
};

/**
 * Overrides applied when a backend service is running inside the docker-compose stack (`CONTAINERIZED=true` set in the service dockerfile). Swaps `localhost` infrastructure hosts for the sibling
 * container's Docker DNS name (a containerized service's `localhost:5432` is its own loopback, not Postgres).
 *
 * Frontend URIs are not overridden — `config.Development` already uses the canonical assets-container path that works in every launch mode.
 */
const containerizedDevOverrides: Record<string, string | number> = {
  INFRA_POSTGRES_HOST: `vertex-echo-infra-postgres-main`,
  INFRA_POSTGRES_URI: `postgres://vertex-echo-infra-postgres-main:${ports.Development.INFRA_POSTGRES_PORT}`,
  INFRA_MONGO_HOST: `vertex-echo-infra-mongodb-main`,
  INFRA_MONGO_URI: `mongodb://vertex-echo-infra-mongodb-main:${ports.Development.INFRA_MONGO_PORT}`,
  INFRA_REDIS_URI: `redis://vertex-echo-infra-redis-main:${ports.Development.INFRA_REDIS_PORT}`,
  // PRODUCT object storage: containers reach MinIO by its sibling Docker DNS name; the host base above uses `localhost:9000` (the published port).
  STORAGE_ENDPOINT: `https://vertex-echo-infra-minio-main:9000`
};

/**
 * Per-service overrides applied when a service container is self-launched on the host (`pnpm launch service-<name>`). Each entry swaps the Kong-routed `SERVICE_<NAME>_URI` for the internal
 * docker-DNS form (`SERVICE_INTERNAL_<NAME>_URI`); `DevKongOverridesMap` then rewrites that target inside Kong to `host.docker.internal:<PORT>`, so containerized callers reach the host-bound dev
 * process transparently.
 *
 * Frontend app URIs are never overridden — see the APPS comment in `config.Development`.
 */
const devOverridesMap: Record<string, Record<string, string>> = {
  'vertex-echo-service-lobby-main': { SERVICE_LOBBY_URI: config.Development.SERVICE_INTERNAL_LOBBY_URI },
  'vertex-echo-service-user-main': { SERVICE_USER_URI: config.Development.SERVICE_INTERNAL_USER_URI },
  'vertex-echo-service-voice-main': { SERVICE_VOICE_URI: config.Development.SERVICE_INTERNAL_VOICE_URI },
  'vertex-echo-service-automation-main': { SERVICE_AUTOMATION_URI: config.Development.SERVICE_INTERNAL_AUTOMATION_URI },
  'vertex-echo-service-billing-main': { SERVICE_BILLING_URI: config.Development.SERVICE_INTERNAL_BILLING_URI }
};

/**
 * Kong upstream rewrites for self-launched services. When a service runs on the host, Kong (still in Docker) must reach it via `host.docker.internal`. The local Kong config builder applies these
 * substitutions to translate each `SERVICE_INTERNAL_<NAME>_URI` (docker DNS form) into the host-bridge URL.
 */
export const DevKongOverridesMap: DevKongOverride[] = [
  { FROM_URI: config.Development.SERVICE_INTERNAL_LOBBY_URI, TO_URI: `http://host.docker.internal:${sharedPorts.SERVICE_LOBBY_PORT}` },
  { FROM_URI: config.Development.SERVICE_INTERNAL_USER_URI, TO_URI: `http://host.docker.internal:${sharedPorts.SERVICE_USER_PORT}` },
  { FROM_URI: config.Development.SERVICE_INTERNAL_VOICE_URI, TO_URI: `http://host.docker.internal:${sharedPorts.SERVICE_VOICE_PORT}` },
  { FROM_URI: config.Development.SERVICE_INTERNAL_AUTOMATION_URI, TO_URI: `http://host.docker.internal:${sharedPorts.SERVICE_AUTOMATION_PORT}` },
  { FROM_URI: config.Development.SERVICE_INTERNAL_BILLING_URI, TO_URI: `http://host.docker.internal:${sharedPorts.SERVICE_BILLING_PORT}` }
];

/** Cached settings map. Populated by the first `GetSharedSettings` call; subsequent calls ignore their arguments and return the same frozen object. */
let settingsMap: SharedSettingsMap | null = null;

/**
 * Returns the active settings map for the given environment, cached after the first call.
 *
 * @param env - Target environment. Falls back to `EnvProcess.ENV_MODE` if omitted.
 * @param skipContainers - Docker-compose service names self-launched on the host. Only `vertex-echo-service-*` entries have an effect (see `devOverridesMap`); other names are ignored.
 * @param clientIP - In Development only, rewrites `localhost` URIs to this IP so a phone/tablet on the LAN can reach the host dev stack.
 * @returns The frozen settings map.
 */
export function GetSharedSettings(env: Environment | undefined = undefined, skipContainers: string[] = [], clientIP?: string): Readonly<SharedSettingsMap> {
  if (settingsMap) return settingsMap;

  const ENV_MODE = env || EnvProcess.ENV_MODE;
  // `CONTAINERIZED=true` is set only by backend service dockerfiles. Host processes (launch.ts, vite dev, scripts) and the browser bundle all see `false`.
  const CONTAINERIZED = EnvProcess.CONTAINERIZED === 'true';

  const active: Partial<SharedSettingsMap> = {
    ...sharedPorts,
    ...config[ENV_MODE],
    ...ports[ENV_MODE]
  };

  // For each self-launched service, swap its Kong-routed URI for the internal docker-DNS URI. Spread LAST so it wins over both `config.Development` and `containerizedDevOverrides`.
  const dynamicDevOverrides: Partial<SharedSettingsMap> = skipContainers.filter((k) => k in devOverridesMap).reduce((acc, k) => ({ ...acc, ...devOverridesMap[k] }), {});

  const activeSettings: SharedSettingsMap =
    ENV_MODE !== Environment.Development
      ? ({ ...active, ENV_MODE, VERSION } as SharedSettingsMap)
      : CONTAINERIZED
        ? ({ ...active, ...containerizedDevOverrides, ...dynamicDevOverrides, ENV_MODE, VERSION } as SharedSettingsMap)
        : ({ ...active, ...dynamicDevOverrides, ENV_MODE, VERSION } as SharedSettingsMap);

  // Containerized Node services must reach the assets container by its Docker DNS name (their own `localhost` is the service's loopback). Browser runtimes keep `localhost`.
  const isBrowserRuntime = typeof globalThis !== 'undefined' && 'window' in globalThis;
  if (ENV_MODE === Environment.Development && CONTAINERIZED && !isBrowserRuntime) {
    activeSettings.ASSETS_URI = `http://${localConstants.assetsContainerHost}`;
  }

  // In Development, if a public HTTPS tunnel (cloudflared) is fronting local Kong, compose the provider callbacks so Vapi, Twilio, and Stripe can reach the local stack. Read here (not in the static
  // `config` table) so they resolve lazily on first call, after all modules are loaded — never relying on env being present when the module-level `config` literal is constructed. See the
  // `VAPI_SERVER_URL` / `TWILIO_STATUS_CALLBACK_URL` / `STRIPE_WEBHOOK_URL` comments above.
  if (ENV_MODE === Environment.Development && EnvProcess.DEV_TUNNEL_URL) {
    activeSettings.VAPI_SERVER_URL = `${EnvProcess.DEV_TUNNEL_URL}${localConstants.vapiWebhookPath}`;
    activeSettings.VAPI_TOOLS_URL = `${EnvProcess.DEV_TUNNEL_URL}${localConstants.vapiToolsPath}`;
    activeSettings.TWILIO_STATUS_CALLBACK_URL = `${EnvProcess.DEV_TUNNEL_URL}${localConstants.twilioWebhookPath}`;
    activeSettings.STRIPE_WEBHOOK_URL = `${EnvProcess.DEV_TUNNEL_URL}${localConstants.stripeWebhookPath}`;
    activeSettings.SCHEDULING_OAUTH_REDIRECT_URL = `${EnvProcess.DEV_TUNNEL_URL}${localConstants.schedulingOAuthCallbackPath}`;
  }

  if (ENV_MODE === Environment.Development && clientIP) {
    ReplaceLocalhostWithIp(activeSettings, clientIP);
  }

  Logger.Log(localConstants.logContext, `Settings Environment: ${ENV_MODE}, Version: ${VERSION}`);
  return (settingsMap = Object.freeze(activeSettings));
}
